// A venue's own logo, shown on the TV while nothing is queued.
//
// It is stored on the server now (see src/helpers/venueLogos.ts), set by the player's
// owner from the control panel or from the TV itself, and every screen showing the player
// gets it. Before that it lived only in the browser of the TV that uploaded it, in
// IndexedDB. A logo stored that way is still shown on that TV until the owner uploads one
// to the server, so no screen loses its logo when this ships.

import { makeRawApiRequest } from './Api';

// Keep these in step with src/helpers/venueLogos.ts.
export const MAX_LOGO_BYTES = 20 * 1024 * 1024;
export const MIN_LOGO_WIDTH = 960;
export const MIN_LOGO_HEIGHT = 540;
export const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg'];

export type Size = { width: number; height: number };

// Half of a 1080p screen one way or the other: a wide logo only needs the width and a tall
// one the height. The TV draws the logo at up to half the screen each way.
export const isLogoLargeEnough = (logo: Size) => logo.width >= MIN_LOGO_WIDTH || logo.height >= MIN_LOGO_HEIGHT;

const readImageSize = (file: Blob): Promise<Size> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      reject(new Error('That file could not be read as an image.'));
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });

// Returns a sentence to show the owner, or null when the file is fine to send. The server
// checks all of this again from the file's own bytes; this is only so the owner hears
// about it before waiting for an upload.
export const checkLogoFile = async (file: File): Promise<string | null> => {
  if (ACCEPTED_LOGO_TYPES.indexOf(file.type) === -1) {
    return 'Use a PNG or JPG image.';
  }
  if (file.size > MAX_LOGO_BYTES) {
    return 'That file is over 20 MB. Use a smaller copy of the logo.';
  }
  let size: Size;
  try {
    size = await readImageSize(file);
  } catch (error) {
    return 'That file could not be read as an image.';
  }
  if (!isLogoLargeEnough(size)) {
    return (
      `That logo is ${size.width} x ${size.height} pixels and would look blurry on a TV. ` +
      `Use one at least ${MIN_LOGO_WIDTH} pixels wide or ${MIN_LOGO_HEIGHT} pixels tall.`
    );
  }
  return null;
};

export const uploadVenueLogo = (playerId: number, file: File, token: string): Promise<{ logoUrl: string }> =>
  makeRawApiRequest(`player/${playerId}/logo`, 'POST', file, file.type, token);

export const deleteVenueLogo = (playerId: number, token: string): Promise<{ logoUrl: null }> =>
  makeRawApiRequest(`player/${playerId}/logo`, 'DELETE', null, null, token);

// -- The old copy kept in this browser ----------------------------------------------

const DB_NAME = 'jookable';
const DB_VERSION = 1;
const STORE = 'venueLogos';

const keyFor = (playerIdentifier: string) => playerIdentifier.toLowerCase();

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('No IndexedDB here.'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const run = async <T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest): Promise<T> => {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result as T);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const loadLocalVenueLogo = async (playerIdentifier: string): Promise<Blob | null> => {
  try {
    const blob = await run<Blob | undefined>('readonly', (store) => store.get(keyFor(playerIdentifier)));
    return blob || null;
  } catch (error) {
    return null;
  }
};

// Called once a logo has been put on the server or removed there, so this browser stops
// showing its old copy.
export const forgetLocalVenueLogo = async (playerIdentifier: string) => {
  try {
    await run('readwrite', (store) => store.delete(keyFor(playerIdentifier)));
  } catch (error) {
    // Nothing stored, or no IndexedDB. Either way there is nothing to show.
  }
};
